import { createSlice, createAsyncThunk } from "@reduxjs/toolkit";
import axios from "axios";
import { ROOT_URL } from "../../api/ApiConfig";
import { mapApiGroupToTableRow } from "../../utils/groupMappers";

const getAuthHeaders = (getState) => {
  const { loginToken } = getState();
  const authToken = loginToken?.response?.token;
  return {
    "Content-Type": "application/json",
    Authorization: `Bearer ${authToken}`,
  };
};

const unwrap = (response) => response?.data;

function mapListResponse(payload) {
  const data = payload?.data;
  if (!Array.isArray(data)) return [];
  return data.map(mapApiGroupToTableRow).filter(Boolean);
}

function buildGroupsListKey(arg = {}) {
  const { status = "all", search } = arg;
  return [status, search || ""].join("::");
}

export const fetchGroupsList = createAsyncThunk(
  "groups/fetchList",
  async ({ status = "all", search, countsOnly } = {}, { getState, rejectWithValue }) => {
    try {
      const params = new URLSearchParams();
      if (status) params.set("status", status);
      if (search) params.set("search", search);

      const qs = params.toString();
      const response = await axios.get(`${ROOT_URL}/group/list${qs ? `?${qs}` : ""}`, {
        headers: getAuthHeaders(getState),
      });
      return { ...unwrap(response), _queryStatus: status || "all" };
    } catch (error) {
      return rejectWithValue(error.response?.data || error.message);
    }
  }
);

/** Groups the current user belongs to / can access — use for select dropdowns. */
export const fetchAccessibleGroups = createAsyncThunk(
  "groups/fetchAccessible",
  async (_, { getState, rejectWithValue }) => {
    try {
      const response = await axios.get(`${ROOT_URL}/group/accessible`, {
        headers: getAuthHeaders(getState),
      });
      return unwrap(response);
    } catch (error) {
      return rejectWithValue(error.response?.data || error.message);
    }
  }
);

export const fetchGroupById = createAsyncThunk(
  "groups/fetchById",
  async ({ groupId }, { getState, rejectWithValue }) => {
    try {
      const response = await axios.get(`${ROOT_URL}/group/profile/${groupId}`, {
        headers: getAuthHeaders(getState),
      });
      return unwrap(response);
    } catch (error) {
      return rejectWithValue(error.response?.data || error.message);
    }
  }
);

export const createGroup = createAsyncThunk(
  "groups/create",
  async (body, { getState, rejectWithValue }) => {
    try {
      const response = await axios.post(`${ROOT_URL}/group/create`, body, {
        headers: getAuthHeaders(getState),
      });
      return unwrap(response);
    } catch (error) {
      return rejectWithValue(error.response?.data || error.message);
    }
  }
);

export const updateGroup = createAsyncThunk(
  "groups/update",
  async ({ groupId, body }, { getState, rejectWithValue }) => {
    try {
      const response = await axios.put(`${ROOT_URL}/group/update/${groupId}`, body, {
        headers: getAuthHeaders(getState),
      });
      return unwrap(response);
    } catch (error) {
      return rejectWithValue(error.response?.data || error.message);
    }
  }
);

export const archiveGroup = createAsyncThunk(
  "groups/archive",
  async ({ groupId }, { getState, rejectWithValue }) => {
    try {
      const response = await axios.post(
        `${ROOT_URL}/group/archive/${groupId}`,
        {},
        { headers: getAuthHeaders(getState) }
      );
      return unwrap(response);
    } catch (error) {
      return rejectWithValue(error.response?.data || error.message);
    }
  }
);

export const activateGroup = createAsyncThunk(
  "groups/activate",
  async ({ groupId }, { getState, rejectWithValue }) => {
    try {
      const response = await axios.post(
        `${ROOT_URL}/group/activate/${groupId}`,
        {},
        { headers: getAuthHeaders(getState) }
      );
      return unwrap(response);
    } catch (error) {
      return rejectWithValue(error.response?.data || error.message);
    }
  }
);

const initialState = {
  list: [],
  listLoading: false,
  listError: null,
  lastFetchedListKey: null,
  cacheByListKey: {},
  tabCounts: {
    all: 0,
    active: 0,
    archived: 0,
  },
  detail: null,
  detailLoading: false,
  detailError: null,
  lastFetchedGroupId: null,
  cacheByGroupId: {},
  mutationLoading: false,
  mutationError: null,
};

const groupsApiSlice = createSlice({
  name: "peopleGroups",
  initialState,
  reducers: {
    clearGroupDetail: (state) => {
      state.detail = null;
      state.detailError = null;
    },
  },
  extraReducers: (builder) => {
    builder
      .addCase(fetchGroupsList.pending, (state, action) => {
        if (action.meta?.arg?.countsOnly) return;
        const key = buildGroupsListKey(action.meta?.arg);
        const cached = key && state.cacheByListKey[key];
        if (cached) {
          state.list = cached;
          state.listLoading = false;
        } else if (key && key !== state.lastFetchedListKey) {
          state.listLoading = true;
        } else {
          state.listLoading = state.list.length === 0;
        }
        state.listError = null;
      })
      .addCase(fetchGroupsList.fulfilled, (state, action) => {
        const counts = action.payload?.meta?.counts;
        if (counts) {
          state.tabCounts = {
            all: counts.all ?? 0,
            active: counts.active ?? 0,
            archived: counts.archived ?? 0,
          };
        }

        if (!action.meta?.arg?.countsOnly) {
          const key = buildGroupsListKey(action.meta?.arg);
          const mapped = mapListResponse(action.payload);
          state.listLoading = false;
          state.list = mapped;
          if (key) {
            state.cacheByListKey[key] = mapped;
            state.lastFetchedListKey = key;
          }
        }
      })
      .addCase(fetchGroupsList.rejected, (state, action) => {
        if (action.meta?.arg?.countsOnly) return;
        const key = buildGroupsListKey(action.meta?.arg);
        state.listLoading = false;
        state.listError = action.payload;
        if (!key || !state.cacheByListKey[key]) {
          state.list = [];
        }
      })
      .addCase(fetchAccessibleGroups.pending, (state) => {
        state.listLoading = state.list.length === 0;
        state.listError = null;
      })
      .addCase(fetchAccessibleGroups.fulfilled, (state, action) => {
        state.listLoading = false;
        const mapped = mapListResponse(action.payload);
        state.list = mapped;
        state.lastFetchedListKey = "accessible";
        state.tabCounts = {
          all: mapped.length,
          active: mapped.filter((g) => g.status === "active").length,
          archived: mapped.filter((g) => g.status === "archived").length,
        };
      })
      .addCase(fetchAccessibleGroups.rejected, (state, action) => {
        state.listLoading = false;
        state.listError = action.payload;
        state.list = [];
      })
      .addCase(fetchGroupById.pending, (state, action) => {
        const idStr =
          action.meta.arg?.groupId != null ? String(action.meta.arg.groupId) : "";
        const cached = idStr && state.cacheByGroupId[idStr];
        const detailId = state.detail?.id || state.detail?._id;
        const inMemorySame =
          idStr &&
          detailId != null &&
          String(detailId) === idStr &&
          state.lastFetchedGroupId != null &&
          String(state.lastFetchedGroupId) === idStr;
        if (cached) {
          state.detail = cached;
          state.detailLoading = false;
        } else if (inMemorySame) {
          state.detailLoading = false;
        } else {
          state.detailLoading = true;
        }
        state.detailError = null;
      })
      .addCase(fetchGroupById.fulfilled, (state, action) => {
        state.detailLoading = false;
        state.detail = action.payload?.data || null;
        const idStr =
          action.meta.arg?.groupId != null ? String(action.meta.arg.groupId) : "";
        if (idStr && state.detail) {
          state.cacheByGroupId[idStr] = state.detail;
          state.lastFetchedGroupId = idStr;
        }
      })
      .addCase(fetchGroupById.rejected, (state, action) => {
        state.detailLoading = false;
        state.detailError = action.payload;
        const idStr =
          action.meta.arg?.groupId != null ? String(action.meta.arg.groupId) : "";
        if (!idStr || !state.cacheByGroupId[idStr]) {
          state.detail = null;
        }
      })
      .addMatcher(
        (action) =>
          [
            createGroup.pending.type,
            updateGroup.pending.type,
            archiveGroup.pending.type,
            activateGroup.pending.type,
          ].includes(action.type),
        (state) => {
          state.mutationLoading = true;
          state.mutationError = null;
        }
      )
      .addMatcher(
        (action) =>
          [
            createGroup.fulfilled.type,
            createGroup.rejected.type,
            updateGroup.fulfilled.type,
            updateGroup.rejected.type,
            archiveGroup.fulfilled.type,
            archiveGroup.rejected.type,
            activateGroup.fulfilled.type,
            activateGroup.rejected.type,
          ].includes(action.type),
        (state, action) => {
          state.mutationLoading = false;
          if (action.type.endsWith("/rejected")) {
            state.mutationError = action.payload;
          }
        }
      );
  },
});

export const { clearGroupDetail } = groupsApiSlice.actions;
export default groupsApiSlice.reducer;
